import { useMemo } from "react";

import {
  CATEGORY_META,
  CATEGORY_ORDER,
  Category,
  Frequency,
  Importance,
  NotificationPref,
  useNotificationPrefs,
  useUpdateNotificationPrefs,
} from "../api/notifications";

const IMPORTANCE_OPTIONS: Importance[] = ["any", "notable", "major"];
const FREQUENCY_OPTIONS: Frequency[] = ["realtime", "daily", "bi_daily"];

const IMPORTANCE_LABEL: Record<Importance, string> = {
  any: "anything",
  notable: "notable+",
  major: "major only",
};
const FREQUENCY_LABEL: Record<Frequency, string> = {
  realtime: "realtime",
  daily: "daily",
  bi_daily: "twice daily",
  off: "off",
};

function defaultPref(category: Category): NotificationPref {
  return {
    category,
    enabled: true,
    importance: "notable",
    frequency: "daily",
    daily_hour: 13,
    daily_hour2: null,
  };
}

export default function NotificationPrefsGrid() {
  const prefs = useNotificationPrefs();
  const update = useUpdateNotificationPrefs();

  // Build a complete prefs list — merge server response with defaults so
  // every category renders even if the server is missing one.
  const byCategory = useMemo(() => {
    const map = new Map<Category, NotificationPref>();
    for (const c of CATEGORY_ORDER) map.set(c, defaultPref(c));
    for (const p of prefs.data ?? []) map.set(p.category, p);
    return map;
  }, [prefs.data]);

  const allOff = CATEGORY_ORDER.every((c) => !byCategory.get(c)?.enabled);

  function patch(category: Category, partial: Partial<NotificationPref>) {
    const next: NotificationPref[] = CATEGORY_ORDER.map((c) => {
      const cur = byCategory.get(c)!;
      if (c === category) return { ...cur, ...partial };
      return cur;
    });
    update.mutate(next);
  }

  function toggleAll(target: boolean) {
    const next: NotificationPref[] = CATEGORY_ORDER.map((c) => ({
      ...byCategory.get(c)!,
      enabled: target,
    }));
    update.mutate(next);
  }

  if (prefs.isLoading) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">
        Loading preferences…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 text-sm">
        <div>
          <div className="font-medium text-slate-200">Mute all</div>
          <div className="text-xs text-slate-500">
            Pause every category. Re-enable individually after.
          </div>
        </div>
        <button
          onClick={() => toggleAll(allOff)}
          className={
            "rounded-full px-3 py-1 text-xs font-medium transition " +
            (allOff
              ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
              : "bg-slate-800 text-slate-300 hover:bg-slate-700")
          }
        >
          {allOff ? "All muted" : "Mute all"}
        </button>
      </div>

      <div className="space-y-2">
        {CATEGORY_ORDER.map((category) => {
          const p = byCategory.get(category)!;
          const meta = CATEGORY_META[category];
          const isScheduled = p.frequency === "daily" || p.frequency === "bi_daily";
          const selectCls =
            "w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-200 disabled:opacity-40";
          const labelCls =
            "mb-1 block text-[10px] uppercase tracking-wide text-slate-500";
          return (
            <div
              key={category}
              className="rounded-lg border border-slate-800 bg-slate-900/30 p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-200">{meta.label}</div>
                  <div className="mt-0.5 text-xs text-slate-500">{meta.blurb}</div>
                </div>
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-800 text-sky-500 focus:ring-sky-500"
                  checked={p.enabled}
                  onChange={(e) => patch(category, { enabled: e.target.checked })}
                  aria-label={`Enable ${meta.label}`}
                />
              </div>

              {/* Stacked on mobile (default), inline on `sm+`. Frequency
                  appears below importance on small screens per layout req. */}
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
                <label>
                  <span className={labelCls}>Importance</span>
                  <select
                    disabled={!p.enabled}
                    value={p.importance}
                    onChange={(e) =>
                      patch(category, { importance: e.target.value as Importance })
                    }
                    className={selectCls}
                  >
                    {IMPORTANCE_OPTIONS.map((v) => (
                      <option key={v} value={v}>
                        {IMPORTANCE_LABEL[v]}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span className={labelCls}>Frequency</span>
                  <select
                    disabled={!p.enabled}
                    value={p.frequency === "off" ? "daily" : p.frequency}
                    onChange={(e) =>
                      patch(category, { frequency: e.target.value as Frequency })
                    }
                    className={selectCls}
                  >
                    {FREQUENCY_OPTIONS.map((v) => (
                      <option key={v} value={v}>
                        {FREQUENCY_LABEL[v]}
                      </option>
                    ))}
                  </select>
                </label>

                {isScheduled && (
                  <label>
                    <span className={labelCls}>Hour (UTC)</span>
                    <div className="flex items-center gap-1">
                      <select
                        disabled={!p.enabled}
                        value={p.daily_hour ?? 13}
                        onChange={(e) =>
                          patch(category, { daily_hour: parseInt(e.target.value, 10) })
                        }
                        className={selectCls}
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <option key={h} value={h}>
                            {String(h).padStart(2, "0")}:00
                          </option>
                        ))}
                      </select>
                      {p.frequency === "bi_daily" && (
                        <select
                          disabled={!p.enabled}
                          value={p.daily_hour2 ?? 21}
                          onChange={(e) =>
                            patch(category, {
                              daily_hour2: parseInt(e.target.value, 10),
                            })
                          }
                          className={selectCls}
                        >
                          {Array.from({ length: 24 }, (_, h) => (
                            <option key={h} value={h}>
                              {String(h).padStart(2, "0")}:00
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {update.isError && (
        <p className="text-xs text-rose-300">
          Couldn&apos;t save: {update.error.message}
        </p>
      )}
    </div>
  );
}
